import {
  FEATURE_MANAGEMENT,
  PERMISSION_DATA,
  ROLE_MANAGEMENT,
  USER_MANAGEMENT_V2,
} from "api/ApiPath";
import { ActionInjection } from "app/actions/di/ActionInjection";
import { ActionUseCase } from "app/actions/domain/useCases/ActionUseCase";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BasePermissionMenu } from "app/based/baseApiModel/basePermissionMenu/BasePermissionMenu";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { RoleManagementInjection } from "features/roleManagement/di/RoleManagementInjection";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { strings } from "utils/localizedStrings";
import {
  ArrayEditActionEntity,
  RoleActionRequestEntity,
} from "../entities/actions/RoleActionRequestEntity";
import { DetailRoleEntity } from "../entities/detail/DetailRoleEntity";
import { RoleRequestEntity } from "../entities/RoleRequestEntity";
import { RoleResponseEntity } from "../entities/RoleResponseEntity";
import { IRoleManagementRepository } from "../repositories/IRoleManagementRepository";

@injectable()
export class RoleManagementUseCase {
  private roleRepo: IRoleManagementRepository;
  private filterUseCase: FilterUseCase;
  private actionUseCase: ActionUseCase;

  constructor() {
    this.actionUseCase = ActionInjection.getUseCase();
    this.roleRepo = RoleManagementInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private checkParams(params: RoleRequestEntity) {
    const finalParams = { ...params };
    delete finalParams.created;

    Object.keys(finalParams).forEach((rawKey) => {
      const key = rawKey as keyof RoleRequestEntity;
      if (
        finalParams[key] === strings.GlobalLabels.All ||
        finalParams[key] === "" ||
        typeof finalParams[key] === "undefined"
      ) {
        delete finalParams[key];
      }
    });

    return finalParams;
  }

  private buildArrayEditBody<T extends Object>(
    bodyVal: Array<string>,
    comparedVal: Array<T>,
  ): ArrayEditActionEntity | undefined {
    const selectedVal = (Array.isArray(bodyVal) ? bodyVal : []).filter(
      Boolean,
    ) as string[];
    const currentVal =
      comparedVal?.map((item) => item?.["code"]).filter(Boolean) || [];

    const add = selectedVal.filter((item) => !currentVal.includes(item));
    const erase = currentVal.filter((item) => !selectedVal.includes(item));

    if (!add.length && !erase.length) {
      return undefined;
    }

    return {
      add: add.length ? add : undefined,
      erase: erase.length ? erase : undefined,
    };
  }

  public updateBody(
    body: RoleActionRequestEntity,
    detailInfo: DetailRoleEntity,
  ) {
    const keysToAvoid = ["code", "menus", "users", "features"];
    const finalMenu = !detailInfo["code"]
      ? body?.menus
      : this.buildArrayEditBody(
          body?.menus as Array<string>,
          detailInfo?.menus,
        );
    const finalUsers = !detailInfo["code"]
      ? body?.users
      : this.buildArrayEditBody(
          body?.users as Array<string>,
          detailInfo?.users,
        );
    const finalFeatures = !detailInfo["code"]
      ? body?.features
      : this.buildArrayEditBody(
          body?.features as Array<string>,
          detailInfo?.features,
        );
    return {
      keysToAvoid,
      afterBody: {
        ...body,
        menus: finalMenu,
        users: finalUsers,
        features: finalFeatures,
      } as typeof body,
    };
  }

  async getList(
    params: RoleRequestEntity,
  ): Promise<ListBaseApiResponseModel<RoleResponseEntity>> {
    return await this.roleRepo.getList(this.checkParams(params));
  }

  async getRoleDetail(
    params: RoleRequestEntity,
  ): Promise<BaseApiResponseModel<DetailRoleEntity>> {
    return await this.roleRepo.getDetail(this.checkParams(params));
  }

  async createRole(
    body: RoleActionRequestEntity,
    detailInfo: DetailRoleEntity,
  ): Promise<BaseApiResponseModel<RoleResponseEntity> | undefined> {
    const updatedBody = this.updateBody(body, detailInfo);
    return await this.actionUseCase.create({
      path: ROLE_MANAGEMENT.CREATE,
      inputValue: updatedBody?.afterBody,
    });
  }

  async updateRole(
    body: RoleActionRequestEntity,
    detailInfo: DetailRoleEntity,
  ): Promise<BaseApiResponseModel<RoleResponseEntity> | undefined> {
    const updatedBody = this.updateBody(body, detailInfo);
    return await this.actionUseCase.update({
      path: ROLE_MANAGEMENT.UPDATE,
      inputValue: updatedBody?.afterBody,
      keysToAvoid: updatedBody?.keysToAvoid,
      comparedValue: detailInfo,
    });
  }

  async deleteRole(
    params: RoleRequestEntity,
  ): Promise<BaseApiResponseModel<RoleResponseEntity> | undefined> {
    return await this.actionUseCase.delete({
      path: ROLE_MANAGEMENT.DELETE,
      inputValue: params,
    });
  }

  async getUsers(
    params: BaseUserRequestModel,
  ): Promise<Array<BaseUserResponseModel>> {
    const path = USER_MANAGEMENT_V2.FILTER_LIST;
    return await this.filterUseCase.fetchListWithFeature({
      path,
      isNeedFeature: true,
      params,
    });
  }

  async getUserPermission(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    const path = FEATURE_MANAGEMENT.USER_FEATURE;
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
      savedKey: CacheKeyEnum.internalFeature,
    });
  }

  async getMenuList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BasePermissionMenu>> {
    const path = PERMISSION_DATA.FILTER_LIST;
    return await this.filterUseCase.fetchListWithFeature({
      path,
      params,
      isNeedFeature: true,
    });
  }
}
